/** @jsxImportSource preact */
import type { ComponentProps, Ref } from "preact";

// 按 shadcn/ui 的 Message、Bubble、Marker 组合适配 Preact 和 Shadow DOM。
// https://ui.shadcn.com/docs/components/base/message
// 上游许可证见 packages/widget/licenses/shadcn-ui.txt。
// 只保留挂件使用的展示插槽，颜色和字号继续由商家主题提供。
export function Message({
  align = "start",
  elementRef,
  ...props
}: ComponentProps<"div"> & {
  align?: "start" | "end";
  elementRef?: Ref<HTMLDivElement>;
}) {
  return (
    <div ref={elementRef} data-slot="message" data-align={align} {...props} />
  );
}

export function MessageContent(props: ComponentProps<"div">) {
  return <div data-slot="message-content" {...props} />;
}

export function MessageAvatar(props: ComponentProps<"div">) {
  return <div data-slot="message-avatar" {...props} />;
}

export function MessageFooter(props: ComponentProps<"div">) {
  return <div data-slot="message-footer" {...props} />;
}

export function Bubble({
  variant = "default",
  ...props
}: ComponentProps<"div"> & {
  variant?: "default" | "secondary" | "ghost";
}) {
  return <div data-slot="bubble" data-variant={variant} {...props} />;
}

export function BubbleContent(props: ComponentProps<"div">) {
  return <div data-slot="bubble-content" {...props} />;
}

export function Marker(props: ComponentProps<"div">) {
  return <div data-slot="marker" {...props} />;
}

export function MarkerContent(props: ComponentProps<"span">) {
  return <span data-slot="marker-content" {...props} />;
}

export const MESSAGE_STYLES = `
.gn-chat-panel, [data-slot="message"] { --gn-avatar-size:24px; --gn-avatar-gap:8px; --gn-avatar-top:0px; }
.gn-chat-panel[data-editorial], [data-slot="message"][data-editorial] { --gn-avatar-size:20px; --gn-avatar-gap:10px; --gn-avatar-top:2px; }
[data-slot="message"] { position:relative; display:flex; width:100%; min-width:0; gap:var(--gn-avatar-gap); }
[data-slot="message"][data-align="end"] { flex-direction:row-reverse; }
[data-slot="message-avatar"] { display:flex; align-self:flex-start; flex:none; width:var(--gn-avatar-size); margin-top:var(--gn-avatar-top); }
/* 角色是行内块，外面那层按文字行排会多出几 px 行高，角色就不再和旁边第一行、生成中那行居中。 */
[data-slot="message-avatar"] > [data-slot="marker-content"] { display:flex; }
[data-slot="message-content"] { display:flex; flex-direction:column; align-items:flex-start; flex:1; min-width:0; gap:10px; overflow-wrap:anywhere; }
.gn-message-followup { box-sizing:border-box; padding-inline-start:calc(var(--gn-avatar-size) + var(--gn-avatar-gap)); }
[data-slot="message"][data-align="end"] > [data-slot="message-content"] { align-items:flex-end; }
/* 尾部现在只放操作行，按正常流排；抵消内容列的部分间距，让时间贴近最后一个消息框。 */
.gn-reply-tail { position:relative; display:flex; flex-direction:column; align-items:flex-start; align-self:flex-start; width:fit-content; max-width:100%; margin-top:-6px; }
[data-slot="message"][data-align="end"] > [data-slot="message-content"] > .gn-reply-tail { align-items:flex-end; align-self:flex-end; }
.gn-reply-tail > .gn-feedback { padding-top:0; }
.gn-product-list { width:100%; }
/* 装得下时靠发言人一侧；溢出时从起点滚动，不能让前面的卡片落到不可滚达的负方向。 */
[data-slot="message"][data-align="end"] > [data-slot="message-content"] > .gn-product-list { justify-content:safe flex-end; }
[data-slot="message-footer"] { display:flex; align-items:center; max-width:100%; min-width:0; gap:5px; padding-inline:14px; font-size:.78em; }
[data-slot="message"]:has([data-variant="ghost"]) [data-slot="message-footer"] { padding-inline:0; }
[data-slot="bubble"] { position:relative; display:flex; flex-direction:column; width:fit-content; max-width:var(--gn-message-max-width); min-width:0; }
[data-slot="bubble-content"] { box-sizing:border-box; width:fit-content; max-width:100%; min-width:0; overflow:hidden; overflow-wrap:anywhere; white-space:pre-wrap; border-radius:var(--gn-message-radius); background:var(--gn-message-bg); color:var(--gn-message-fg); padding:var(--gn-message-padding); line-height:var(--gn-message-leading); }
/* 只有组首第一块气泡朝发言人缩角：左侧收左上角，右侧收右上角。 */
[data-slot="message"][data-group-start][data-align="start"] > [data-slot="message-content"] > [data-slot="bubble"]:first-child > [data-slot="bubble-content"] { border-start-start-radius:calc(var(--gn-message-radius) / 4); }
[data-slot="message"][data-group-start][data-align="end"] > [data-slot="message-content"] > [data-slot="bubble"]:first-child > [data-slot="bubble-content"] { border-start-end-radius:calc(var(--gn-message-radius) / 4); }
[data-slot="bubble"][data-variant="ghost"] > [data-slot="bubble-content"] { border-radius:0; }
/* 操作行、快捷回答是面板挂在消息后面的兄弟节点：正文流完之前不出现，流完从 display:none 恢复时各自的入场动画正好开始播。快捷回答写的是内联 display，所以要 !important。 */
[data-slot="message"][data-revealing] ~ * { display:none !important; }
[data-slot="marker"] { position:relative; display:flex; align-items:center; width:100%; min-width:0; min-height:16px; gap:8px; text-align:start; }
[data-slot="marker-content"] { min-width:0; overflow-wrap:anywhere; }
`;
